import { useEffect, useState, useCallback } from "react";
import { projects } from "../data/projects";
import {
  fetchSettings,
  fetchCustomProjects,
  fetchStaticOverrides,
  adminLogin,
  saveSettings,
  createCustomProject,
  updateCustomProject,
  deleteCustomProject,
  uploadMedia,
  updateStaticOverride,
  deleteStaticOverride,
} from "../lib/settingsApi";
import { fetchTitles } from "../lib/titlesApi";
import { resolveStaticTitle, resolveLegacyTitle } from "../lib/titleResolver";
import CaseStudyEditor from "../components/CaseStudyEditor";
import TypographyPanel from "../components/TypographyPanel";
import AssetLibrary from "../components/AssetLibrary";
import AssetPicker from "../components/AssetPicker";

const TOKEN_KEY = "admin-token";
const ALLOWED_FILTERS = ["none", "grayscale", "warm", "cool"];
// Absolute path prefix for URLs returned by the /api/admin/upload endpoint.
// The backend returns `/api/media/{id}` (a bare path) so it works across
// preview/prod without hard-coded domains — we prepend REACT_APP_BACKEND_URL
// only when we need to load the media from JS. For the DB / <img src> path
// we store the relative URL directly.
const backendBase = process.env.REACT_APP_BACKEND_URL || "";
const resolveMediaUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/api/media/")) return `${backendBase}${u}`;
  return u;
};

/** Resolve the thumbnail image URL for a project row (static or custom). */
const resolveThumb = (row) => {
  if (row.kind === "custom") {
    // Uploaded media URLs come back from the backend as relative
    // `/api/media/{id}` paths; prepend REACT_APP_BACKEND_URL so they load
    // via CORS from the admin origin without any additional setup.
    return resolveMediaUrl(row.media_poster || row.media_src || "");
  }
  // Static row — prefer an admin-uploaded hero image (row.override) over
  // the baked-in defaults from projects.js. `override` holds a pending or
  // saved static_override; the same shape used for custom rows.
  const ov = row.override || {};
  if (ov.media_src || ov.media_poster) {
    return resolveMediaUrl(ov.media_poster || ov.media_src);
  }
  const m = row.project?.media || {};
  if (m.type === "video") return m.poster || row.project.transitionImage || m.src;
  return m.src || row.project.transitionImage || m.poster;
};

/** Build a payload for the create/duplicate endpoint from any row shape. */
const rowToCustomPayload = (row) => {
  if (row.kind === "custom") {
    return {
      title: `${row.title} (copy)`,
      slug: "", // never carry the slug over — case-study routing is static
      media_type: row.media_type,
      media_src: row.media_src,
      media_poster: row.media_poster,
      filter: row.filter,
    };
  }
  const m = row.project.media || {};
  return {
    title: `${row.project.title} (copy)`,
    slug: "",
    media_type: m.type || "image",
    media_src: m.src || "",
    media_poster: m.poster || "",
    filter: row.project.filter || "none",
  };
};

/**
 * Hidden `/admin` page. NOT linked from anywhere on the public site.
 *
 * Layout: a visual contact-sheet grid — one card per project. Static
 * projects (the base list defined in `data/projects.js`) are non-editable
 * but can be hidden, reordered, and DUPLICATED. Duplicating a static card
 * creates a NEW custom project that IS fully editable (title, media, filter).
 * The admin can also create a blank custom project via the "+ New project"
 * card at the end.
 *
 * Per-card actions:
 *   • drag to reorder (any card)
 *   • ← / → touch fallback (any card)
 *   • visibility checkbox (any card)
 *   • duplicate (any card — always produces a NEW custom row)
 *   • edit (custom cards only — inline title + media fields)
 *   • delete (custom cards only)
 */
export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  // Unified row shape:
  //   { kind: "static", index, title, project, hidden, order }
  //   { kind: "custom", id, title, media_type, media_src, media_poster,
  //                     filter, slug, hidden, order }
  // Array position === current display order.
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  // Which section of /admin is currently visible. "projects" → the contact
  // sheet (default). "typography" → the TypographyPanel that manages the
  // project-headline font family/size/weight/etc. globally. Kept as local
  // state (not URL-synced) because /admin is a hidden route and the whole
  // page mounts fresh on each visit.
  const [activeTab, setActiveTab] = useState("projects");

  // Which Edit-panel field (if any) is currently waiting for the admin to
  // pick an image from the Asset Library. `null` closes the picker; a
  // string of shape `${rowKey}:src` or `${rowKey}:poster` records both the
  // target row AND which field to write back. Kept up here so the picker
  // modal can render at the page root regardless of which row is open.
  const [pickerTarget, setPickerTarget] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // Snapshot of the row's editable fields at the moment Edit is opened.
  // Used by the Cancel button to restore pre-edit values without touching
  // the backend. Keyed by rowKey (`admin-row-{i}` or `admin-custom-{id}`)
  // so simultaneous edits on different rows (shouldn't happen — one at a
  // time — but defensive) don't clobber each other. Cleared on Done or
  // Cancel so a subsequent Edit re-snapshots the *current* state.
  const [editSnapshots, setEditSnapshots] = useState({});
  // Track file-drag hover + upload progress PER CARD (keyed by card index)
  // so multiple simultaneous uploads don't clobber each other's UI state.
  const [fileOverIdx, setFileOverIdx] = useState(null);
  const [uploads, setUploads] = useState({}); // { [idx]: { pct: 0-100, error?: string } }
  // { slug, title } | null — when set, the CaseStudyEditor modal is open.
  const [caseStudySlug, setCaseStudySlug] = useState(null);

  // ---------- Load ----------
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    Promise.all([
      fetchSettings(),
      fetchCustomProjects(),
      fetchStaticOverrides(),
      // Legacy per-index title overrides (/api/titles). Portfolio.jsx uses
      // these as the "middle layer" between static_overrides.title (new)
      // and the source-code project.title, so we must include them here or
      // the Edit panel shows the source default while the LIVE page shows
      // the legacy override — exactly the bug the user reported.
      fetchTitles().catch(() => ({})),
    ])
      .then(([map, custom, sov, titles]) => {
        if (cancelled) return;
        const titleOverrides = titles || {};
        const staticRows = projects.map((p, i) => {
          const ov = sov[String(i)] || {};
          // Effective LIVE title uses the shared resolver so Admin.jsx and
          // Portfolio.jsx can't drift out of sync. See /app/frontend/src/lib/titleResolver.js.
          const legacyTitle = resolveLegacyTitle(i, titleOverrides);
          const liveTitle = resolveStaticTitle(i, sov, titleOverrides, p);
          return {
            kind: "static",
            index: i,
            project: p,
            // `title` displays whatever the visitor sees — override wins.
            title: liveTitle,
            // `override` holds the editable diff; blank strings for unset
            // fields so the edit inputs are controlled.
            override: {
              title: ov.title || "",
              media_type: ov.media_type || (p.media?.type || "image"),
              media_src: ov.media_src || "",
              media_poster: ov.media_poster || "",
            },
            // Original values used for the "Reset to default" affordance
            // AND (via `legacy_title`) as the pre-fill fallback in the
            // Edit panel when no static_overrides.title is set yet.
            defaults: {
              title: p.title,
              legacy_title: legacyTitle,
              media_type: p.media?.type || "image",
              media_src: p.media?.src || "",
              media_poster: p.media?.poster || "",
            },
            hidden: Boolean(map[String(i)]?.hidden),
            order: typeof map[String(i)]?.order === "number" ? map[String(i)].order : i,
          };
        });
        const customRows = (custom || []).map((c) => ({
          kind: "custom",
          id: c.id,
          title: c.title,
          slug: c.slug || "",
          media_type: c.media_type || "image",
          media_src: c.media_src || "",
          media_poster: c.media_poster || "",
          filter: c.filter || "none",
          hidden: Boolean(c.hidden),
          order: typeof c.order === "number" ? c.order : 9999,
        }));
        const combined = [...staticRows, ...customRows].sort((a, b) => a.order - b.order);
        setRows(combined);
        setLoaded(true);
      })
      .catch(() => {
        setRows(
          projects.map((p, i) => ({
            kind: "static",
            index: i,
            project: p,
            title: p.title,
            override: {
              title: "",
              media_type: p.media?.type || "image",
              media_src: "",
              media_poster: "",
            },
            defaults: {
              title: p.title,
              media_type: p.media?.type || "image",
              media_src: p.media?.src || "",
              media_poster: p.media?.poster || "",
            },
            hidden: false,
            order: i,
          }))
        );
        setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // ---------- Auth ----------
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoggingIn(true);
    try {
      const { token: t } = await adminLogin(password);
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setPassword("");
    } catch (err) {
      setLoginError(err?.response?.status === 401 ? "Incorrect password" : "Sign-in failed");
    } finally {
      setLoggingIn(false);
    }
  };
  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setRows([]);
    setLoaded(false);
    setEditingId(null);
  };

  // ---------- Row mutations (state-only until Save) ----------
  const toggleHidden = (i) =>
    setRows((rs) => {
      setSaveMsg("");
      return rs.map((r, idx) => (idx === i ? { ...r, hidden: !r.hidden } : r));
    });

  const move = useCallback((from, to) => {
    if (from === to || from < 0 || to < 0) return;
    setRows((rs) => {
      if (to >= rs.length) return rs;
      const next = rs.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
    setSaveMsg("");
  }, []);

  const updateCustomField = (id, field, value) => {
    setRows((rs) => rs.map((r) => (r.kind === "custom" && r.id === id ? { ...r, [field]: value } : r)));
    setSaveMsg("");
  };

  /** Patch a specific field of a STATIC row's `override` object. Also
   * mirrors the title into `row.title` so the collapsed card preview
   * reflects the pending change immediately. */
  const updateStaticOverrideField = (index, field, value) => {
    setRows((rs) =>
      rs.map((r) => {
        if (!(r.kind === "static" && r.index === index)) return r;
        const nextOverride = { ...r.override, [field]: value };
        return {
          ...r,
          override: nextOverride,
          // Keep displayed title in sync while typing. When the admin
          // clears the field entirely, fall back to the legacy /titles
          // override (what visitors actually see on the live page) BEFORE
          // the source-code default — mirrors the load-effect precedence.
          title:
            field === "title"
              ? value || r.defaults.legacy_title || r.defaults.title
              : r.title,
        };
      })
    );
    setSaveMsg("");
  };

  /** Reset a static row's override → will delete the DB doc on next Save. */
  const resetStaticOverride = (index) => {
    setRows((rs) =>
      rs.map((r) => {
        if (!(r.kind === "static" && r.index === index)) return r;
        return {
          ...r,
          // After reset the displayed title falls back through the same
          // chain the LIVE page uses: legacy /titles override, else the
          // source-code default. Never blank.
          title: r.defaults.legacy_title || r.defaults.title,
          override: { title: "", media_type: r.defaults.media_type, media_src: "", media_poster: "" },
        };
      })
    );
    setSaveMsg("Cleared override — Save to apply");
  };

  /** Merge multiple field changes onto a custom row in a single setRows
   * pass. Used after a successful upload so `media_src`, `media_type`,
   * and any resolved poster land together and the row re-renders once. */
  const patchCustomRow = (id, patch) => {
    setRows((rs) => rs.map((r) => (r.kind === "custom" && r.id === id ? { ...r, ...patch } : r)));
    setSaveMsg("");
  };

  // ---------- Edit / Cancel snapshot helpers ----------

  /** Open the Edit panel for a row and snapshot its current editable
   * fields so Cancel can restore them. Snapshot lives in `editSnapshots`
   * keyed by rowKey — cleared on Done or Cancel to keep the map tiny. */
  const beginEdit = (row, rowKey) => {
    const snapshot =
      row.kind === "custom"
        ? {
            kind: "custom",
            title: row.title || "",
            media_type: row.media_type || "image",
            media_src: row.media_src || "",
            media_poster: row.media_poster || "",
            filter: row.filter || "none",
          }
        : {
            kind: "static",
            // Store the whole override object so Cancel restores exactly
            // what was there — including "no override at all" (undefined).
            override: row.override ? { ...row.override } : undefined,
            title: row.title,
          };
    setEditSnapshots((prev) => ({ ...prev, [rowKey]: snapshot }));
    setEditingId(rowKey);
  };

  /** Close the Edit panel and DISCARD the snapshot without touching
   * row state — a Save-and-close semantic. */
  const endEdit = (rowKey) => {
    setEditSnapshots((prev) => {
      if (!(rowKey in prev)) return prev;
      const next = { ...prev };
      delete next[rowKey];
      return next;
    });
    setEditingId(null);
  };

  /** Restore the row to its pre-edit state from the snapshot, then close
   * the Edit panel. If somehow the snapshot is missing (should never
   * happen — beginEdit always sets it) just close without changes. */
  const cancelEdit = (rowKey) => {
    const snap = editSnapshots[rowKey];
    if (snap) {
      setRows((rs) =>
        rs.map((r) => {
          // rowKey format matches the one built in the map iteration:
          // `c-{id}` for custom rows, `s-{index}` for static rows. Must
          // match here or the row lookup silently fails and Cancel becomes
          // a no-op restoration.
          const rk = r.kind === "custom" ? `c-${r.id}` : `s-${r.index}`;
          if (rk !== rowKey) return r;
          if (snap.kind === "custom") {
            return {
              ...r,
              title: snap.title,
              media_type: snap.media_type,
              media_src: snap.media_src,
              media_poster: snap.media_poster,
              filter: snap.filter,
            };
          }
          // Static: restore the override map (or drop it if there wasn't
          // one) and the displayed title.
          return {
            ...r,
            override: snap.override ? { ...snap.override } : {},
            title: snap.title,
          };
        })
      );
    }
    endEdit(rowKey);
    setSaveMsg("Cancelled — no changes made");
    setTimeout(() => setSaveMsg((m) => (m === "Cancelled — no changes made" ? "" : m)), 2500);
  };

  /**
   * Handle an uploaded File — validate type client-side (fast reject
   * before hitting the network), push to /api/admin/upload, then patch
   * the row with the new media_type + media_src. Only wired to CUSTOM
   * cards because static ones are immutable in the admin.
   */
  const handleFileUpload = async (rowIdx, file) => {
    if (!file) return;
    const ct = (file.type || "").toLowerCase();
    if (!ct.startsWith("image/") && !ct.startsWith("video/")) {
      setUploads((u) => ({ ...u, [rowIdx]: { pct: 0, error: "Unsupported file type" } }));
      setTimeout(() => setUploads((u) => { const n = { ...u }; delete n[rowIdx]; return n; }), 2500);
      return;
    }
    const row = rows[rowIdx];
    if (!row) return;

    setUploads((u) => ({ ...u, [rowIdx]: { pct: 0 } }));
    try {
      const res = await uploadMedia(file, token, (e) => {
        if (!e.total) return;
        const pct = Math.round((e.loaded / e.total) * 100);
        setUploads((u) => ({ ...u, [rowIdx]: { pct } }));
      });
      if (row.kind === "custom") {
        patchCustomRow(row.id, {
          media_src: res.url,
          media_type: res.media_type,
          ...(res.media_type === "image" ? { media_poster: "" } : {}),
        });
      } else {
        // Static row — write into the row's `override` object. Videos need
        // a separate poster upload; images clear any leftover poster.
        updateStaticOverrideField(row.index, "media_type", res.media_type);
        updateStaticOverrideField(row.index, "media_src", res.url);
        if (res.media_type === "image") {
          updateStaticOverrideField(row.index, "media_poster", "");
        }
      }
      setSaveMsg(`Uploaded ${res.media_type} — remember to Save`);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Upload failed";
      setUploads((u) => ({ ...u, [rowIdx]: { pct: 0, error: String(detail).slice(0, 80) } }));
      setTimeout(() => setUploads((u) => { const n = { ...u }; delete n[rowIdx]; return n; }), 3500);
      return;
    }
    // Success — clear the progress overlay.
    setUploads((u) => { const n = { ...u }; delete n[rowIdx]; return n; });
  };

  // ---------- Create / Duplicate / Delete (immediate API calls) ----------
  const handleDuplicate = async (i) => {
    setSaveMsg("");
    try {
      const payload = rowToCustomPayload(rows[i]);
      // Insert the duplicate immediately after its source so the user sees
      // it appear right next to what they clicked.
      const created = await createCustomProject(payload, token);
      const inserted = {
        kind: "custom",
        id: created.id,
        title: created.title,
        slug: created.slug || "",
        media_type: created.media_type || "image",
        media_src: created.media_src || "",
        media_poster: created.media_poster || "",
        filter: created.filter || "none",
        hidden: Boolean(created.hidden),
        order: created.order,
      };
      setRows((rs) => {
        const next = rs.slice();
        next.splice(i + 1, 0, inserted);
        return next;
      });
      setSaveMsg("Duplicated — remember to Save");
    } catch (err) {
      setSaveMsg(err?.response?.status === 401 ? "Session expired" : "Duplicate failed");
    }
  };

  const handleCreateBlank = async () => {
    setSaveMsg("");
    try {
      const created = await createCustomProject(
        {
          title: "Untitled project",
          media_type: "image",
          media_src: "",
          filter: "none",
        },
        token
      );
      const inserted = {
        kind: "custom",
        id: created.id,
        title: created.title,
        slug: created.slug || "",
        media_type: created.media_type || "image",
        media_src: created.media_src || "",
        media_poster: created.media_poster || "",
        filter: created.filter || "none",
        hidden: Boolean(created.hidden),
        order: created.order,
      };
      // Append at the end so the "+ New" tile stays last in the visible grid.
      setRows((rs) => [...rs, inserted]);
      setEditingId(`c-${created.id}`);
      setSaveMsg("New project — fill in details, then Save");
    } catch (err) {
      setSaveMsg(err?.response?.status === 401 ? "Session expired" : "Create failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this project? This can't be undone.")) return;
    setSaveMsg("");
    try {
      await deleteCustomProject(id, token);
      setRows((rs) => rs.filter((r) => !(r.kind === "custom" && r.id === id)));
      if (editingId === `c-${id}`) setEditingId(null);
    } catch (err) {
      setSaveMsg(err?.response?.status === 401 ? "Session expired" : "Delete failed");
    }
  };

  // ---------- Bulk save ----------
  const handleSave = async () => {
    setSaving(true);
    setSaveMsg("");
    try {
      // Persist STATIC visibility+order in one bulk call.
      const staticPayload = rows
        .map((r, i) => (r.kind === "static" ? { index: r.index, hidden: r.hidden, order: i } : null))
        .filter(Boolean);
      await saveSettings(staticPayload, token);

      // Persist per-STATIC-row title + hero-media overrides. For each static
      // row: if any override field has a value → PUT that patch; if all are
      // empty AND the user had been editing this row → DELETE the doc to
      // fully reset. Skips rows the user hasn't touched (title === default,
      // no media_src, no media_poster) to avoid needless writes.
      await Promise.all(
        rows
          .filter((r) => r.kind === "static")
          .map(async (r) => {
            const ov = r.override || {};
            const hasTitle = ov.title && ov.title !== r.defaults.title;
            const hasMedia = Boolean(ov.media_src) || Boolean(ov.media_poster);
            if (hasTitle || hasMedia) {
              const patch = {};
              if (hasTitle) patch.title = ov.title;
              if (hasMedia) {
                patch.media_type = ov.media_type || r.defaults.media_type;
                if (ov.media_src) patch.media_src = ov.media_src;
                if (ov.media_poster) patch.media_poster = ov.media_poster;
              }
              await updateStaticOverride(r.index, patch, token);
            } else {
              // Idempotent — a 404 here (no doc to delete) is fine.
              // Any other status should bubble up so the outer catch can
              // handle 401 (session expired) or surface a save failure.
              try {
                await deleteStaticOverride(r.index, token);
              } catch (err) {
                if (err?.response?.status !== 404) throw err;
              }
            }
          })
      );

      // Persist CUSTOM projects one-by-one — full field set + order + hidden.
      // Small collection (usually < 10), so N+1 is fine and simpler than a
      // dedicated bulk endpoint. Send the row's array position as `order`
      // so admin drag results are respected across static + custom boundaries.
      await Promise.all(
        rows.map((r, i) => {
          if (r.kind !== "custom") return null;
          return updateCustomProject(
            r.id,
            {
              title: r.title,
              slug: r.slug,
              media_type: r.media_type,
              media_src: r.media_src,
              media_poster: r.media_poster,
              filter: r.filter,
              hidden: r.hidden,
              order: i,
            },
            token
          );
        })
      );
      setSaveMsg("Saved");
      setTimeout(() => setSaveMsg(""), 2500);
    } catch (err) {
      if (err?.response?.status === 401) {
        handleLogout();
        setLoginError("Session expired. Please sign in again.");
      } else {
        setSaveMsg("Save failed");
      }
    } finally {
      setSaving(false);
    }
  };

  // ---------- DnD ----------
  // Two overlapping drag interactions coexist on each card:
  //   1. Card-to-card reorder — internal drag with dataTransfer set to
  //      the source array index. Only fires on custom + static cards.
  //   2. Externally-dragged FILE from the OS — dataTransfer.types
  //      includes "Files". Only meaningful on custom cards (upload).
  // We detect (2) by `types.includes("Files")` and branch accordingly.
  const isFileDrag = (e) => {
    try {
      return Array.from(e.dataTransfer.types || []).includes("Files");
    } catch {
      return false;
    }
  };

  const onDragStart = (i) => (e) => {
    setDragIdx(i);
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(i));
    } catch {
      /* ignore */
    }
  };
  const onDragOver = (i) => (e) => {
    // preventDefault is REQUIRED to allow drop; without it the browser
    // refuses to fire onDrop (both for reorder AND file uploads).
    e.preventDefault();
    if (isFileDrag(e)) {
      e.dataTransfer.dropEffect = "copy";
      if (fileOverIdx !== i) setFileOverIdx(i);
    } else {
      e.dataTransfer.dropEffect = "move";
      if (overIdx !== i) setOverIdx(i);
    }
  };
  const onDragLeave = () => {
    // Clear the file-hover indicator when the pointer exits the card.
    // Card-reorder overIdx is cleared on drop/dragEnd instead — this only
    // matters for the file-drop overlay which relies on hover state.
    setFileOverIdx(null);
  };
  const onDrop = (i) => (e) => {
    e.preventDefault();
    if (isFileDrag(e)) {
      const file = e.dataTransfer.files?.[0];
      const row = rows[i];
      if (file && row) {
        // Both static and custom cards accept file drops now — static
        // rows write into their `override.media_*` fields.
        handleFileUpload(i, file);
      }
      setFileOverIdx(null);
      return;
    }
    if (dragIdx != null) move(dragIdx, i);
    setDragIdx(null);
    setOverIdx(null);
  };
  const onDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
    setFileOverIdx(null);
  };

  // ---------- Login gate ----------
  if (!token) {
    return (
      <div
        data-testid="admin-login"
        className="min-h-screen bg-paper text-ink flex items-center justify-center px-6"
      >
        <form
          onSubmit={handleLogin}
          className="w-full max-w-sm border border-ink/10 bg-white p-8 shadow-sm"
        >
          <div className="font-sans text-[10px] tracking-[0.4em] uppercase text-ink/50 mb-6">
            Admin
          </div>
          <label htmlFor="admin-password" className="block font-sans text-[13px] text-ink/70 mb-2">
            Password
          </label>
          <input
            id="admin-password"
            data-testid="admin-password-input"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            className="w-full border border-ink/20 bg-white px-3 py-2 font-sans text-[15px] focus:border-ink outline-none"
          />
          {loginError && (
            <div data-testid="admin-login-error" className="mt-3 font-sans text-[12px] text-red-600">
              {loginError}
            </div>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={loggingIn || !password}
            className="mt-6 w-full bg-ink text-paper py-2.5 font-sans text-[11px] tracking-[0.32em] uppercase disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            {loggingIn ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- Contact sheet ----------
  return (
    <div data-testid="admin-panel" className="min-h-screen bg-paper text-ink">
      <div className="sticky top-0 z-20 bg-paper/95 backdrop-blur border-b border-ink/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-baseline gap-4 md:gap-6 min-w-0">
            <div className="font-sans text-[10px] tracking-[0.4em] uppercase text-ink/50">Admin</div>
            <h1 className="font-display text-[1.15rem] md:text-[1.4rem] font-medium tracking-[-0.02em] truncate">
              {activeTab === "typography"
                ? "Typography"
                : activeTab === "assets"
                ? "Asset library"
                : "Portfolio contact sheet"}
            </h1>
            {/* Tab switcher — kept minimal, matches existing tracking/typography.
                Only surfaced once logged-in. Two tabs: Projects (default) and
                Typography (new). No URL routing because /admin is a hidden
                single-page route. */}
            <nav
              data-testid="admin-tab-nav"
              className="hidden md:flex items-center gap-1 ml-2 pl-4 border-l border-ink/15"
            >
              {[
                { id: "projects", label: "Projects" },
                { id: "typography", label: "Typography" },
                { id: "assets", label: "Assets" },
              ].map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(t.id);
                    setSaveMsg("");
                  }}
                  data-testid={`admin-tab-${t.id}`}
                  className={`font-sans text-[10px] tracking-[0.32em] uppercase px-3 py-1.5 transition-colors ${
                    activeTab === t.id
                      ? "text-ink border-b border-ink"
                      : "text-ink/50 hover:text-ink"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            {saveMsg && (
              <span
                data-testid="admin-save-status"
                className={`font-sans text-[12px] ${
                  /fail(ed)?/i.test(saveMsg)
                    ? "text-red-600"
                    : "text-emerald-600"
                }`}
              >
                {saveMsg}
              </span>
            )}
            {activeTab === "projects" && (
              <button
                type="button"
                onClick={handleSave}
                disabled={saving || !loaded}
                data-testid="admin-save-button"
                className="bg-ink text-paper px-5 py-2 font-sans text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 hover:opacity-90 transition-opacity"
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            )}
            <button
              type="button"
              onClick={handleLogout}
              data-testid="admin-logout"
              className="hidden md:inline font-sans text-[10px] tracking-[0.32em] uppercase text-ink/60 hover:text-ink transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
        {/* Mobile-only tab switcher (below the top bar, since we hide the
            nav on mobile above to save space). */}
        <nav
          data-testid="admin-tab-nav-mobile"
          className="md:hidden max-w-[1400px] mx-auto px-5 pb-3 flex items-center gap-1 border-t border-ink/10 pt-3"
        >
          {[
            { id: "projects", label: "Projects" },
            { id: "typography", label: "Typography" },
            { id: "assets", label: "Assets" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => {
                setActiveTab(t.id);
                setSaveMsg("");
              }}
              data-testid={`admin-tab-mobile-${t.id}`}
              className={`font-sans text-[10px] tracking-[0.32em] uppercase px-3 py-1.5 transition-colors ${
                activeTab === t.id
                  ? "text-ink border-b border-ink"
                  : "text-ink/50 hover:text-ink"
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 md:px-8 pt-6 pb-16">
        {activeTab === "typography" ? (
          <TypographyPanel
            token={token}
            onSaveMessage={(m) => {
              setSaveMsg(m);
              // Auto-clear the toast after a few seconds so it doesn't
              // linger when the admin switches tabs.
              setTimeout(() => setSaveMsg(""), 3500);
            }}
          />
        ) : activeTab === "assets" ? (
          <AssetLibrary
            token={token}
            onSaveMessage={(m) => {
              setSaveMsg(m);
              setTimeout(() => setSaveMsg(""), 3500);
            }}
          />
        ) : (
          <>
        <p className="font-sans text-[13px] md:text-[14px] text-ink/60 leading-relaxed mb-6 max-w-2xl">
          Drag any card to re-order. Toggle visibility with the checkbox.
          Duplicate any card to create a new editable copy, or use{" "}
          <em className="not-italic font-medium text-ink/80">+ New project</em>{" "}
          for a blank one. Static (built-in) projects can be reordered and
          hidden but not renamed. Press{" "}
          <em className="not-italic font-medium text-ink/80">Save changes</em>{" "}
          to persist everything.
        </p>

        {!loaded ? (
          <div className="font-sans text-[13px] text-ink/50">Loading…</div>
        ) : (
          <ul
            data-testid="admin-project-list"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          >
            {rows.map((r, i) => {
              const rowKey = r.kind === "custom" ? `c-${r.id}` : `s-${r.index}`;
              const rowTestId = r.kind === "custom" ? `admin-custom-${r.id}` : `admin-row-${r.index}`;
              const isCustom = r.kind === "custom";
              // Unified editing gate — the same `editingId` state holds
              // either `s-{index}` or `c-{uuid}` so only one card is open
              // at a time regardless of kind.
              const isEditing = editingId === rowKey;
              const thumb = resolveThumb(r);
              // For custom rows the row IS the editable state; for static
              // rows edits live in r.override.*.
              // For custom rows the row IS the editable state; for static
              // rows edits live in r.override.*. When there's no override
              // yet (fresh install) the input MUST fall back to the
              // source-code default so the admin can see and tweak the
              // current live value instead of typing it from scratch.
              // Empty-string override values are the schema's way of
              // saying "no override, use default" (see resetStaticOverride
              // and the initial load), so `||` fallthrough is intentional
              // for statics — an admin who *deliberately* clears a field
              // and saves is expressing "revert to default" anyway.
              const editSrc = isCustom
                ? (r.media_src || "")
                : (r.override?.media_src || r.defaults?.media_src || "");
              const editType = isCustom
                ? (r.media_type || "image")
                : (r.override?.media_type || r.defaults?.media_type || "image");
              const editPoster = isCustom
                ? (r.media_poster || "")
                : (r.override?.media_poster || r.defaults?.media_poster || "");
              const editTitle = isCustom
                ? (r.title || "")
                : (r.override?.title
                    || r.defaults?.legacy_title
                    || r.defaults?.title
                    || "");
              const editingSetTitle = (v) =>
                isCustom
                  ? updateCustomField(r.id, "title", v)
                  : updateStaticOverrideField(r.index, "title", v);
              const editingSetType = (v) =>
                isCustom
                  ? updateCustomField(r.id, "media_type", v)
                  : updateStaticOverrideField(r.index, "media_type", v);
              const editingSetSrc = (v) =>
                isCustom
                  ? updateCustomField(r.id, "media_src", v)
                  : updateStaticOverrideField(r.index, "media_src", v);
              const editingSetPoster = (v) =>
                isCustom
                  ? updateCustomField(r.id, "media_poster", v)
                  : updateStaticOverrideField(r.index, "media_poster", v);
              return (
                <li
                  key={rowKey}
                  draggable={!isEditing}
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop(i)}
                  onDragEnd={onDragEnd}
                  data-testid={rowTestId}
                  className={`group relative bg-white border shadow-[0_1px_0_rgba(0,0,0,0.02)] flex flex-col select-none transition-all duration-150 ${
                    isCustom ? "border-ink/20" : "border-ink/10"
                  } ${
                    overIdx === i && dragIdx !== i
                      ? "ring-2 ring-ink ring-offset-2 ring-offset-paper"
                      : ""
                  } ${
                    fileOverIdx === i && isCustom
                      ? "ring-2 ring-emerald-500 ring-offset-2 ring-offset-paper"
                      : ""
                  } ${dragIdx === i ? "opacity-40" : ""} ${
                    isEditing ? "cursor-default" : "cursor-grab active:cursor-grabbing"
                  }`}
                >
                  <div
                    className={`relative w-full aspect-video overflow-hidden bg-ink/5 ${
                      r.hidden ? "opacity-30 grayscale" : ""
                    }`}
                  >
                    {thumb ? (
                      <img
                        src={thumb}
                        alt=""
                        loading="lazy"
                        draggable={false}
                        className="w-full h-full object-cover pointer-events-none"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-sans text-[10px] tracking-[0.3em] uppercase text-ink/30">
                        No preview
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-paper/90 backdrop-blur px-2 py-1 font-sans text-[10px] tracking-[0.28em] tabular-nums text-ink/70">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    {isCustom && (
                      <div className="absolute top-2 left-14 bg-ink text-paper px-2 py-1 font-sans text-[9px] tracking-[0.28em] uppercase">
                        Custom
                      </div>
                    )}
                    {r.hidden && (
                      <div className="absolute top-2 right-2 bg-ink text-paper px-2 py-1 font-sans text-[9px] tracking-[0.28em] uppercase">
                        Hidden
                      </div>
                    )}
                    {!isEditing && (
                      <div
                        aria-hidden
                        className="absolute inset-0 flex items-center justify-center pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150 bg-ink/10 font-sans text-[10px] tracking-[0.32em] uppercase text-paper"
                      >
                        {isCustom ? "Drag to reorder or drop a file" : "Drag to reorder"}
                      </div>
                    )}
                    {/* File-drag hover overlay — appears when the user
                        drags an OS file (image/video) over a custom card.
                        Stays green to visually distinguish from the ink
                        reorder ring. */}
                    {fileOverIdx === i && isCustom && !uploads[i] && (
                      <div
                        aria-hidden
                        className="absolute inset-0 flex flex-col items-center justify-center bg-emerald-500/85 text-white pointer-events-none"
                      >
                        <span className="font-sans text-[10px] tracking-[0.32em] uppercase mb-1">
                          Drop to upload
                        </span>
                        <span className="font-sans text-[11px] opacity-90">
                          image or video
                        </span>
                      </div>
                    )}
                    {/* Upload progress overlay — appears while a file is
                        uploading to /api/admin/upload. Also renders errors
                        (which auto-dismiss after ~3 s in handleFileUpload). */}
                    {uploads[i] && (
                      <div
                        data-testid={`admin-upload-progress-${i}`}
                        className={`absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-3 text-center ${
                          uploads[i].error ? "bg-red-600/90 text-white" : "bg-ink/85 text-paper"
                        }`}
                      >
                        {uploads[i].error ? (
                          <>
                            <span className="font-sans text-[10px] tracking-[0.32em] uppercase mb-1">
                              Upload failed
                            </span>
                            <span className="font-sans text-[11px] opacity-90">
                              {uploads[i].error}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className="font-sans text-[10px] tracking-[0.32em] uppercase mb-2">
                              Uploading… {uploads[i].pct}%
                            </span>
                            <div className="w-3/4 h-[3px] bg-paper/20 overflow-hidden">
                              <div
                                className="h-full bg-paper transition-all duration-150 ease-out"
                                style={{ width: `${uploads[i].pct}%` }}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="p-3 md:p-4 flex flex-col gap-3">
                    {/* Title row (with edit form for custom projects) */}
                    {isEditing ? (
                      <div className="space-y-2">
                        <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50">
                          Title
                          <input
                            type="text"
                            value={editTitle}
                            onChange={(e) => editingSetTitle(e.target.value)}
                            placeholder={isCustom ? "" : r.defaults?.title}
                            data-testid={`${rowTestId}-title-input`}
                            className="mt-1 w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[14px] text-ink focus:border-ink outline-none"
                          />
                        </label>
                        <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50">
                          Media type
                          <select
                            value={editType}
                            onChange={(e) => editingSetType(e.target.value)}
                            data-testid={`${rowTestId}-type-select`}
                            className="mt-1 w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[13px] text-ink focus:border-ink outline-none"
                          >
                            <option value="image">Image</option>
                            <option value="video">Video</option>
                          </select>
                        </label>
                        <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50">
                          {editType === "video" ? "Video URL (mp4/webm)" : "Image URL"}
                          <input
                            type="url"
                            value={editSrc}
                            onChange={(e) => editingSetSrc(e.target.value)}
                            data-testid={`${rowTestId}-src-input`}
                            placeholder="https://…  or upload below"
                            className="mt-1 w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[13px] text-ink focus:border-ink outline-none"
                          />
                        </label>
                        {/* Click-to-upload alternative to drag-drop. */}
                        <div className="flex items-center gap-3 flex-wrap">
                          <label className="cursor-pointer inline-flex items-center gap-2 px-3 py-1.5 border border-ink/25 hover:border-ink font-sans text-[10px] tracking-[0.28em] uppercase text-ink/70 hover:text-ink transition-colors">
                            <input
                              type="file"
                              accept="image/*,video/*"
                              className="hidden"
                              data-testid={`${rowTestId}-upload-input`}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) handleFileUpload(i, f);
                                e.target.value = "";
                              }}
                            />
                            Choose file
                          </label>
                          <button
                            type="button"
                            onClick={() => setPickerTarget(`${rowKey}:src`)}
                            data-testid={`${rowTestId}-pick-from-library`}
                            className="px-3 py-1.5 border border-ink/25 hover:border-ink font-sans text-[10px] tracking-[0.28em] uppercase text-ink/70 hover:text-ink transition-colors"
                          >
                            Pick from library
                          </button>
                          <span className="font-sans text-[10px] text-ink/40">
                            or drop onto the thumbnail
                          </span>
                        </div>
                        {editType === "video" && (
                          <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50">
                            Poster image URL
                            <div className="mt-1 flex items-stretch gap-2">
                              <input
                                type="url"
                                value={editPoster}
                                onChange={(e) => editingSetPoster(e.target.value)}
                                data-testid={`${rowTestId}-poster-input`}
                                placeholder="https://…"
                                className="flex-1 border border-ink/20 bg-white px-2 py-1.5 font-sans text-[13px] text-ink focus:border-ink outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => setPickerTarget(`${rowKey}:poster`)}
                                data-testid={`${rowTestId}-poster-pick-from-library`}
                                className="shrink-0 px-3 border border-ink/25 hover:border-ink font-sans text-[10px] tracking-[0.28em] uppercase text-ink/70 hover:text-ink transition-colors"
                              >
                                Pick
                              </button>
                            </div>
                          </label>
                        )}
                        {isCustom && (
                          <label className="block font-sans text-[10px] tracking-[0.25em] uppercase text-ink/50">
                            Filter
                            <select
                              value={r.filter}
                              onChange={(e) => updateCustomField(r.id, "filter", e.target.value)}
                              className="mt-1 w-full border border-ink/20 bg-white px-2 py-1.5 font-sans text-[13px] text-ink focus:border-ink outline-none"
                            >
                              {ALLOWED_FILTERS.map((f) => (
                                <option key={f} value={f}>
                                  {f}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => endEdit(rowKey)}
                            data-testid={`${rowTestId}-done`}
                            className="bg-ink text-paper px-3 py-1.5 font-sans text-[10px] tracking-[0.28em] uppercase hover:opacity-90 transition-opacity"
                          >
                            Done
                          </button>
                          <button
                            type="button"
                            onClick={() => cancelEdit(rowKey)}
                            data-testid={`${rowTestId}-cancel`}
                            className="border border-ink/25 px-3 py-1.5 font-sans text-[10px] tracking-[0.28em] uppercase text-ink/70 hover:text-ink hover:border-ink transition-colors"
                          >
                            Cancel
                          </button>
                          {isCustom ? (
                            <button
                              type="button"
                              onClick={() => handleDelete(r.id)}
                              data-testid={`admin-custom-${r.id}-delete`}
                              className="font-sans text-[10px] tracking-[0.28em] uppercase text-red-600 hover:text-red-800 transition-colors"
                            >
                              Delete
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => resetStaticOverride(r.index)}
                              data-testid={`admin-row-${r.index}-reset`}
                              className="font-sans text-[10px] tracking-[0.28em] uppercase text-ink/50 hover:text-ink transition-colors"
                              title="Reset to the baked-in default"
                            >
                              Reset to default
                            </button>
                          )}
                          <span className="ml-auto font-sans text-[10px] text-ink/40">
                            Remember to Save
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="flex-1 min-w-0">
                          <div
                            className={`font-sans text-[13px] md:text-[14px] leading-tight truncate ${
                              r.hidden ? "text-ink/40 line-through" : "text-ink"
                            }`}
                            title={r.title}
                          >
                            {r.title}
                          </div>
                        </div>

                        {/* Move earlier / later */}
                        <button
                          type="button"
                          onClick={() => move(i, i - 1)}
                          disabled={i === 0}
                          data-testid={`${rowTestId}-up`}
                          className="font-sans text-ink/50 hover:text-ink disabled:opacity-20 disabled:cursor-not-allowed px-1.5 py-1 text-[13px]"
                          aria-label="Move earlier"
                        >
                          ←
                        </button>
                        <button
                          type="button"
                          onClick={() => move(i, i + 1)}
                          disabled={i === rows.length - 1}
                          data-testid={`${rowTestId}-down`}
                          className="font-sans text-ink/50 hover:text-ink disabled:opacity-20 disabled:cursor-not-allowed px-1.5 py-1 text-[13px]"
                          aria-label="Move later"
                        >
                          →
                        </button>

                        {/* Visibility */}
                        <label className="flex items-center cursor-pointer select-none pl-1">
                          <input
                            type="checkbox"
                            checked={!r.hidden}
                            onChange={() => toggleHidden(i)}
                            data-testid={`${rowTestId}-visible`}
                            className="h-4 w-4 accent-ink cursor-pointer"
                          />
                        </label>
                      </div>
                    )}

                    {/* Secondary action row: duplicate / edit — always visible */}
                    {!isEditing && (
                      <div className="flex items-center gap-4 -mt-1">
                        <button
                          type="button"
                          onClick={() => handleDuplicate(i)}
                          data-testid={`${rowTestId}-duplicate`}
                          className="font-sans text-[10px] tracking-[0.28em] uppercase text-ink/60 hover:text-ink transition-colors"
                        >
                          Duplicate
                        </button>
                        <button
                          type="button"
                          onClick={() => beginEdit(r, rowKey)}
                          data-testid={`${rowTestId}-edit`}
                          className="font-sans text-[10px] tracking-[0.28em] uppercase text-ink/60 hover:text-ink transition-colors"
                        >
                          Edit
                        </button>
                        {/* Case-study editor — available for BOTH static
                            and custom rows. Static rows resolve `/project/:slug`
                            via the source-code `projects` array; custom rows
                            use the backend-generated `custom-{shortid}` slug
                            that lives on the row itself. */}
                        {(!isCustom || r.slug) && (
                          <button
                            type="button"
                            onClick={() =>
                              setCaseStudySlug({
                                slug: isCustom ? r.slug : r.project.slug,
                                title: isCustom
                                  ? r.title || "Untitled"
                                  : r.title || r.defaults?.title || r.project.slug,
                              })
                            }
                            data-testid={`${rowTestId}-case-study`}
                            className="font-sans text-[10px] tracking-[0.28em] uppercase text-ink/60 hover:text-ink transition-colors"
                          >
                            Case study
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}

            {/* Persistent "+ New project" tile — always at end of grid */}
            <li
              className="border border-dashed border-ink/25 bg-white/50 hover:bg-white flex items-center justify-center min-h-[220px] cursor-pointer transition-colors"
              onClick={handleCreateBlank}
              data-testid="admin-new-project"
            >
              <div className="flex flex-col items-center gap-2 text-ink/60 hover:text-ink transition-colors">
                <span className="font-sans text-3xl leading-none">+</span>
                <span className="font-sans text-[10px] tracking-[0.32em] uppercase">
                  New project
                </span>
              </div>
            </li>
          </ul>
        )}
          </>
        )}
      </div>

      {caseStudySlug && (
        <CaseStudyEditor
          slug={caseStudySlug.slug}
          projectTitle={caseStudySlug.title}
          token={token}
          onClose={() => setCaseStudySlug(null)}
        />
      )}

      {/* ---- Asset picker modal — mounted at page root so it overlays
             every edit-open row. `pickerTarget` shape: "${rowKey}:src" or
             ":poster". Splitting on ':' lets a single modal drive both
             text inputs without prop-drilling into every row. ---- */}
      <AssetPicker
        token={token}
        open={Boolean(pickerTarget)}
        onClose={() => setPickerTarget(null)}
        onPick={(url) => {
          if (!pickerTarget) return;
          const [rowKey, field] = pickerTarget.split(":");
          // Look up the row via rowKey (`s-{i}` or `c-{id}` — same keys
          // used by beginEdit/cancelEdit) so we can dispatch to the
          // matching setter regardless of static/custom kind.
          const isCustom = rowKey.startsWith("c-");
          if (isCustom) {
            const id = rowKey.slice(2);
            if (field === "src") updateCustomField(id, "media_src", url);
            else if (field === "poster") updateCustomField(id, "media_poster", url);
          } else {
            const idx = parseInt(rowKey.slice(2), 10);
            if (!Number.isNaN(idx)) {
              if (field === "src") updateStaticOverrideField(idx, "media_src", url);
              else if (field === "poster") updateStaticOverrideField(idx, "media_poster", url);
            }
          }
        }}
      />
    </div>
  );
}
